import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField as MuiTextField,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, TextField } from 'ui/baseComponents'
import { extractApiErrorMessage } from '../../api/apiErrors'

const CampaignForm = ({ open, onClose, onSubmit, initialValues, tags }) => {
  const [formData, setFormData] = useState({
    name: '',
    tagId: '',
    description: '',
    priority: '0',
    startDate: '',
    endDate: '',
    active: true,
  })
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (initialValues) {
      setFormData({
        name: initialValues.name || '',
        tagId: initialValues.tagId || '',
        description: initialValues.description || '',
        priority: String(initialValues.priority ?? 0),
        startDate: initialValues.startDate || '',
        endDate: initialValues.endDate || '',
        active: initialValues.active ?? true,
      })
    } else {
      setFormData({
        name: '',
        tagId: '',
        description: '',
        priority: '0',
        startDate: '',
        endDate: '',
        active: true,
      })
    }
    setError(null)
  }, [initialValues, open])

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = useCallback(async () => {
    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }
    if (!formData.tagId) {
      setError('Tag is required')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(formData)
    } catch (err) {
      setError(`Failed to save campaign: ${extractApiErrorMessage(err)}`)
    } finally {
      setSubmitting(false)
    }
  }, [formData, onSubmit])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialValues ? 'Edit Campaign' : 'New Campaign'}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '8px !important',
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Name"
          value={formData.name}
          onChange={handleChange('name')}
          required
          size="small"
        />
        <Autocomplete
          disableClearable
          value={(tags || []).find((t) => t.id === formData.tagId) || null}
          onChange={(e, newValue) => {
            setFormData((prev) => ({
              ...prev,
              tagId: newValue ? newValue.id : '',
            }))
          }}
          options={tags || []}
          getOptionLabel={(t) => `${t.tag} (${t.id})`}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              size="small"
              label="Tag"
              required
              placeholder="Search tags..."
            />
          )}
        />
        <TextField
          label="Description"
          value={formData.description}
          onChange={handleChange('description')}
          multiline
          rows={2}
          size="small"
        />
        <TextField
          label="Priority"
          value={formData.priority}
          onChange={handleChange('priority')}
          type="number"
          size="small"
          helperText="Higher number = higher priority"
        />
        <TextField
          label="Start Date"
          type="date"
          value={formData.startDate}
          onChange={handleChange('startDate')}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="End Date"
          type="date"
          value={formData.endDate}
          onChange={handleChange('endDate')}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <FormControlLabel
          control={
            <Switch
              checked={formData.active}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  active: e.target.checked,
                }))
              }
            />
          }
          label="Active"
        />
      </DialogContent>
      <DialogActions>
        <Button type="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CampaignForm
